import Ajv2020 from 'ajv/dist/2020';
import {
	canonicalize,
	djb2Hash,
	emitRule,
	emitRulesByKeyword,
	extractRulesFromSchema,
	NUMERIC_OPERATORS,
	xAllOfToRules,
} from './components/xallof/serialization';
import { extractCustomValidators, CUSTOM_VALIDATOR_KEYS } from './custom-validators';
import { flattenFields } from './components/xallof/paths';
import {
	isLeafDescriptionBlob,
	parseLeafDescription,
	serializeLeafDescription,
} from './leaf-description';

// Parse a base property's `description` (which may be a `<root>` XML blob
// produced by the Excel CONCAT template) once and cache by reference, so the
// four structured-description diff descriptors below don't re-parse the same
// blob on every render.
const leafDescCache = new WeakMap();
function getParsedLeafDesc(baseProp) {
	if (!baseProp || typeof baseProp !== 'object') return null;
	if (leafDescCache.has(baseProp)) return leafDescCache.get(baseProp);
	const parsed = parseLeafDescription(baseProp.description);
	leafDescCache.set(baseProp, parsed);
	return parsed;
}

/**
 * ajv instance for schema shape validation — draft 2020-12 to match the Go
 * producer validator (numeric exclusiveMinimum/Maximum, contains semantics).
 */
const ajv = new Ajv2020({ allErrors: true, strict: false });

// Meta-schema: validates that a schema body has the required structural shape
const schemaShapeValidator = ajv.compile({
	type: 'object',
	required: ['type'],
	properties: {
		type: { type: 'string' },
	},
});

/**
 * Validate that a JSON Schema object has the correct structural shape.
 * Checks: root must have `type`, all entries in `properties` must have `type`.
 * Does NOT evaluate x-allOf — that's the Go runtime's job.
 * @param {object} schema - The JSON Schema object to validate
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateSchemaShape(schema) {
	const errors = [];

	if (!schema || typeof schema !== 'object') {
		return { valid: false, errors: ['Schema must be a valid object'] };
	}

	// Use ajv to validate root shape
	if (!schemaShapeValidator(schema)) {
		errors.push('Root schema must have a "type" property');
	}

	function validateProperties(properties, path) {
		if (!properties || typeof properties !== 'object') return;
		for (const [key, value] of Object.entries(properties)) {
			const fieldPath = path ? `${path}.${key}` : key;
			if (!value || typeof value !== 'object') {
				errors.push(`Property "${fieldPath}" must be an object`);
				continue;
			}
			// JSON Schema 2020-12 permits a typeless field when it constrains
			// values via `enum` or `const` — the accept set is then the union
			// of those literals' types. Required `type` only when neither is
			// present so we don't reject valid round-trips of typeless enum
			// fields uploaded from external schemas.
			if (!value.type && !Array.isArray(value.enum) && !Object.prototype.hasOwnProperty.call(value, 'const')) {
				errors.push(`Property "${fieldPath}" is missing "type"`);
			}
			if (value.properties) {
				validateProperties(value.properties, fieldPath);
			}
			if (value.items?.properties) {
				validateProperties(value.items.properties, `${fieldPath}[]`);
			}
		}
	}

	if (schema.properties) {
		validateProperties(schema.properties, '');
	}

	return { valid: errors.length === 0, errors };
}

export const SAMPLE_VALUES = {
	string: 'string',
	integer: 0,
	number: 0,
	boolean: false,
	date: '2025-10-30',
	'date-time': '2025-10-30T10:08:12.208000Z',
	array: [],
	object: {},
};

export function hasExampleValue(value) {
	return value !== '' && value !== null && value !== undefined;
}

export function formatExampleValueForInput(value) {
	if (!hasExampleValue(value)) return '';
	if (typeof value === 'string') return value;
	if (typeof value === 'number' || typeof value === 'boolean') return String(value);
	return JSON.stringify(value);
}

function parseIntegerExampleValue(rawValue) {
	if (!/^-?\d+$/.test(rawValue)) {
		return { error: 'Example Value must be a valid integer' };
	}
	return { value: Number.parseInt(rawValue, 10) };
}

function parseBooleanExampleValue(rawValue) {
	if (typeof rawValue === 'boolean') {
		return { value: rawValue };
	}

	const normalizedValue = String(rawValue).toLowerCase();
	if (normalizedValue === 'true') return { value: true };
	if (normalizedValue === 'false') return { value: false };
	return { error: 'Example Value must be "true" or "false"' };
}

function parseDateExampleValue(rawValue) {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
		return { error: 'Example Value must be in YYYY-MM-DD format' };
	}

	const parsedDate = new Date(`${rawValue}T00:00:00Z`);
	if (Number.isNaN(parsedDate.getTime()) || parsedDate.toISOString().slice(0, 10) !== rawValue) {
		return { error: 'Example Value must be a real calendar date' };
	}

	return { value: rawValue };
}

function parseDateTimeExampleValue(rawValue) {
	const parsedDateTime = new Date(rawValue);
	if (Number.isNaN(parsedDateTime.getTime())) {
		return { error: 'Example Value must be a valid date-time' };
	}

	return { value: rawValue };
}

const EXAMPLE_VALUE_PARSERS = {
	integer: parseIntegerExampleValue,
	boolean: parseBooleanExampleValue,
	date: parseDateExampleValue,
	'date-time': parseDateTimeExampleValue,
};

export function parseExampleValue(exampleValue, dataType) {
	if (!hasExampleValue(exampleValue)) {
		return { value: '' };
	}

	const rawValue = typeof exampleValue === 'string' ? exampleValue.trim() : exampleValue;
	const parser = EXAMPLE_VALUE_PARSERS[dataType];
	return parser ? parser(rawValue) : { value: exampleValue };
}

export function formatSchemaBusinessTerm(key, value, parentKey) {
	if (key === 'attachments') return 'Attachments';
	if (parentKey === 'attachments') {
		return key.replaceAll('_', ' ').replaceAll(/\b\w/g, char => char.toUpperCase());
	}
	return value?.title || key;
}

export function getExpectedBusinessTerm(fieldKey, baseProp, parentPath) {
	return formatSchemaBusinessTerm(fieldKey, baseProp, parentPath);
}

export function resolveSchemaFieldType(value) {
	if (Array.isArray(value?.type)) {
		// Skip 'null' so e.g. `["null", "string"]` resolves to "string". The
		// nullable flag (extracted separately) carries the null companion.
		return value.type.find(t => t !== 'null') || 'string';
	}
	return value?.type || 'string';
}

export function getFieldDepth(fieldId) {
	if (!fieldId) return 0;
	return fieldId.split('.').length - 1;
}

export function updateFieldInTree(fields, fieldId, updater) {
	return fields.map(field => {
		if (field.fieldId === fieldId) {
			return updater(field);
		}
		if (field.children?.length > 0) {
			return { ...field, children: updateFieldInTree(field.children, fieldId, updater) };
		}
		return field;
	});
}

export function removeFieldFromTree(fields, fieldId) {
	return fields
		.filter(field => field.fieldId !== fieldId)
		.map(field => {
			if (field.children?.length > 0) {
				return { ...field, children: removeFieldFromTree(field.children, fieldId) };
			}
			return field;
		});
}

export function addChildToField(fields, parentFieldId, childField) {
	return fields.map(field => {
		if (field.fieldId === parentFieldId) {
			return { ...field, children: [...(field.children || []), childField], status: field.status || 'Modified' };
		}
		if (field.children?.length > 0) {
			return { ...field, children: addChildToField(field.children, parentFieldId, childField) };
		}
		return field;
	});
}

export function sortFieldsAttachmentsLast(fields) {
	return [...fields].sort((a, b) => {
		const aKey = (a.dataElementName || '').toLowerCase();
		const bKey = (b.dataElementName || '').toLowerCase();
		if (aKey === 'attachments') return 1;
		if (bKey === 'attachments') return -1;
		return 0;
	});
}

/**
 * Move a field one position up or down within its sibling group.
 * - Root siblings honour the attachments-last invariant: the `attachments` field
 *   cannot be moved, and no field can be moved past it.
 * - Nested children are reordered freely within their parent's children array.
 * - Pure / immutable: returns the same reference when the move is a no-op.
 *
 * @param {Array} fields - the fields tree
 * @param {string} fieldId
 * @param {'up' | 'down'} direction
 * @param {boolean} [isRoot=true]
 * @returns {Array}
 */
export function moveFieldInTree(fields, fieldId, direction, isRoot = true) {
	if (!Array.isArray(fields) || (direction !== 'up' && direction !== 'down')) {
		return fields;
	}

	const display = isRoot ? sortFieldsAttachmentsLast(fields) : fields;
	const idx = display.findIndex(f => f.fieldId === fieldId);

	if (idx !== -1) {
		const fieldName = (display[idx].dataElementName || '').toLowerCase();
		if (isRoot && fieldName === 'attachments') return fields;

		const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
		if (targetIdx < 0 || targetIdx >= display.length) return fields;

		const targetName = (display[targetIdx].dataElementName || '').toLowerCase();
		if (isRoot && targetName === 'attachments') return fields;

		const next = [...display];
		[next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
		return next;
	}

	let changed = false;
	const next = fields.map(field => {
		if (!field.children || field.children.length === 0) return field;
		const updatedChildren = moveFieldInTree(field.children, fieldId, direction, false);
		if (updatedChildren !== field.children) {
			changed = true;
			return { ...field, children: updatedChildren };
		}
		return field;
	});
	return changed ? next : fields;
}

export function formatDataType(field) {
	return field.dataType === 'array' && field.arrayItemType ? `array<${field.arrayItemType}>` : field.dataType;
}

function getActiveChildren(field) {
	return (field.children || []).filter(child => child.status !== 'Deleted');
}

function getDefaultSampleValue(dataType) {
	return SAMPLE_VALUES[dataType] ?? 'string';
}

export function buildSampleValue(propSchema) {
	if (!propSchema) return 'string';
	const type = resolveSchemaFieldType(propSchema);

	if (type === 'object' && propSchema.properties) {
		return Object.fromEntries(
			Object.entries(propSchema.properties).map(([key, value]) => [key, buildSampleValue(value)])
		);
	}

	if (type === 'array' && propSchema.items) {
		return [buildSampleValue(propSchema.items)];
	}

	return {
		...SAMPLE_VALUES,
		date: '2026-01-01',
		'date-time': '2026-01-01T00:00:00Z',
	}[type] ?? 'string';
}

export function buildSampleFromChildren(children) {
	return Object.fromEntries(
		sortFieldsAttachmentsLast(children)
			.filter(child => child.status !== 'Deleted')
			.map(child => {
				const activeChildren = getActiveChildren(child);
				if (child.dataType === 'object' && activeChildren.length > 0) {
					return [child.dataElementName, buildSampleFromChildren(activeChildren)];
				}
				if (child.dataType === 'array') {
					return [child.dataElementName, buildArraySampleFromField(child)];
				}
				return [child.dataElementName, hasExampleValue(child.exampleValue) ? child.exampleValue : getDefaultSampleValue(child.dataType)];
			})
	);
}

export function buildArraySampleFromField(field) {
	const itemType = field.arrayItemType || 'object';
	const activeChildren = getActiveChildren(field);

	if (itemType === 'object' && activeChildren.length > 0) {
		return [buildSampleFromChildren(activeChildren)];
	}

	if (itemType === 'array' && activeChildren.length === 1) {
		return [buildArraySampleFromField(activeChildren[0])];
	}

	return [hasExampleValue(field.exampleValue) ? field.exampleValue : getDefaultSampleValue(itemType)];
}

function buildFieldPayloadTemplate(field, propSchema) {
	const activeChildren = getActiveChildren(field);
	if (field.dataElementName === 'attachments' && field.dataType === 'array' && activeChildren.length === 0) {
		return [{ filename: 'document.pdf', file_content: 'string' }];
	}

	if (field.dataType === 'array') {
		return (activeChildren.length > 0 || field.arrayItemType)
			? buildArraySampleFromField(field)
			: (propSchema ? buildSampleValue(propSchema) : []);
	}

	if (activeChildren.length > 0) {
		return buildSampleFromChildren(activeChildren);
	}

	if (propSchema) {
		return buildSampleValue(propSchema);
	}

	return hasExampleValue(field.exampleValue) ? field.exampleValue : getDefaultSampleValue(field.dataType);
}

export function buildPayloadTemplate(fields, baseSchema) {
	const baseProps = baseSchema?.properties || {};
	return Object.fromEntries(
		sortFieldsAttachmentsLast(fields)
			.filter(field => field.status !== 'Deleted')
			.map(field => [field.dataElementName, buildFieldPayloadTemplate(field, baseProps[field.dataElementName])])
	);
}

function collectDescendantFields(field, parentPath, list) {
	getActiveChildren(field).forEach(child => {
		const childPath = `${parentPath}.${child.dataElementName}`;
		list.push({ ...child, displayPath: childPath });
		collectDescendantFields(child, childPath, list);
	});
}

function getBaseContext(field, baseProps, parentPath) {
	const originalKey = field.fieldId && !field.fieldId.startsWith('new-') && !field.fieldId.includes('.')
		? field.fieldId
		: field.dataElementName;
	const baseKey = baseProps[originalKey] ? originalKey : field.dataElementName;
	return { baseKey, baseProp: baseProps[baseKey] };
}

// ============ Field-level diff: declarative constraint descriptors ============

function isNullish(value) {
	return value == null;
}

function defaultNormalize(value) {
	return value === '' ? null : value;
}

function defaultEquals(a, b) {
	return a === b;
}

function formatScalar(value) {
	return isNullish(value) ? '—' : String(value);
}

function formatBoolean(value) {
	if (value === true) return 'Yes';
	if (value === false) return 'No';
	return '—';
}

function stableStringify(value) {
	if (value === null || typeof value !== 'object') return JSON.stringify(value);
	if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
	const keys = Object.keys(value).sort();
	return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
}

function normalizeEnum(value) {
	if (!Array.isArray(value) || value.length === 0) return null;
	return [...value].map(stableStringify).sort();
}

function enumEquals(a, b) {
	const na = normalizeEnum(a);
	const nb = normalizeEnum(b);
	if (na === null || nb === null) return na === nb;
	if (na.length !== nb.length) return false;
	return na.every((v, i) => v === nb[i]);
}

function formatEnumValue(value) {
	if (!Array.isArray(value) || value.length === 0) return '—';
	return value.map(v => (typeof v === 'string' ? v : stableStringify(v))).join(', ');
}

function exampleEquals(a, b) {
	return stableStringify(a) === stableStringify(b);
}

function formatExample(value) {
	if (isNullish(value) || value === '') return '—';
	const s = typeof value === 'string' ? value : stableStringify(value);
	return s.length > 80 ? `${s.slice(0, 77)}…` : s;
}

/**
 * Field-constraint descriptors: each entry declares how to read the baseline
 * value from a JSON Schema property and how to compare it to the UI field's
 * current value. Order here drives the row order in the confirm-modal diff.
 */
const CONSTRAINT_PROPS = [
	{
		key: 'dataType',
		label: 'Data Type',
		baseFrom: (_baseKey, baseProp) => resolveSchemaFieldType(baseProp),
	},
	{
		key: 'businessTerm',
		label: 'Business Term',
		baseFrom: (baseKey, baseProp, _baseRequired, parentPath) => getExpectedBusinessTerm(baseKey, baseProp, parentPath),
	},
	{
		key: 'mandatory',
		label: 'Mandatory',
		baseFrom: (baseKey, _baseProp, baseRequired) => (baseRequired || []).includes(baseKey),
		format: formatBoolean,
	},
	{
		key: 'format',
		label: 'Format',
		baseFrom: (_baseKey, baseProp) => baseProp?.format ?? null,
	},
	{
		key: 'pattern',
		label: 'Pattern (Regex)',
		baseFrom: (_baseKey, baseProp) => baseProp?.pattern ?? null,
	},
	{
		key: 'minLength',
		label: 'Min Length',
		baseFrom: (_baseKey, baseProp) => baseProp?.minLength ?? null,
	},
	{
		key: 'maxLength',
		label: 'Max Length',
		baseFrom: (_baseKey, baseProp) => baseProp?.maxLength ?? null,
	},
	{
		key: 'minimum',
		label: 'Minimum',
		baseFrom: (_baseKey, baseProp) => baseProp?.minimum ?? null,
	},
	{
		key: 'maximum',
		label: 'Maximum',
		baseFrom: (_baseKey, baseProp) => baseProp?.maximum ?? null,
	},
	{
		key: 'exclusiveMinimum',
		label: 'Exclusive Minimum',
		baseFrom: (_baseKey, baseProp) => baseProp?.exclusiveMinimum ?? null,
	},
	{
		key: 'exclusiveMaximum',
		label: 'Exclusive Maximum',
		baseFrom: (_baseKey, baseProp) => baseProp?.exclusiveMaximum ?? null,
	},
	{
		key: 'multipleOf',
		label: 'Multiple Of',
		baseFrom: (_baseKey, baseProp) => baseProp?.multipleOf ?? null,
	},
	{
		key: 'minItems',
		label: 'Min Items',
		baseFrom: (_baseKey, baseProp) => baseProp?.minItems ?? null,
	},
	{
		key: 'maxItems',
		label: 'Max Items',
		baseFrom: (_baseKey, baseProp) => baseProp?.maxItems ?? null,
	},
	{
		// uniqueItems defaults to `false` in JSON Schema, so normalize false
		// (and unset) to null; only `true` counts as a constraint.
		key: 'uniqueItems',
		label: 'Unique Items',
		baseFrom: (_baseKey, baseProp) => (baseProp?.uniqueItems === true ? true : null),
		normalize: (value) => (value === true ? true : null),
		format: formatBoolean,
	},
	{
		key: 'enumValues',
		label: 'Enum Values',
		baseFrom: (_baseKey, baseProp) => (Array.isArray(baseProp?.enum) && baseProp.enum.length > 0 ? baseProp.enum : null),
		normalize: (value) => (Array.isArray(value) && value.length > 0 ? value : null),
		equals: enumEquals,
		format: formatEnumValue,
	},
	{
		key: 'arrayItemType',
		label: 'Array Item Type',
		// Mirror extractSchemaFields (index.js:284): array fields default a
		// missing items.type to 'object'; non-array fields have no item type.
		baseFrom: (_baseKey, baseProp) =>
			resolveSchemaFieldType(baseProp) === 'array'
				? (baseProp?.items?.type || 'object')
				: null,
	},
	{
		// When `baseProp.description` is the production `<root>` blob, the diff
		// must compare the parsed `description` sub-field against the editor's
		// `field.description` (which extractSchemaFields already parsed out of
		// the blob). Without this, every field's `description` row in the
		// confirm diff falsely shows the FULL XML blob → plain string, even
		// when the user changed nothing.
		key: 'description',
		label: 'Field Description',
		baseFrom: (_baseKey, baseProp) => {
			const parsed = getParsedLeafDesc(baseProp);
			return parsed ? parsed.description : (baseProp?.description ?? '');
		},
	},
	{
		key: 'businessDefinition',
		label: 'Business Definition',
		baseFrom: (_baseKey, baseProp) => {
			const parsed = getParsedLeafDesc(baseProp);
			return parsed ? parsed.businessDefinition : '';
		},
	},
	{
		key: 'applicableStandardName',
		label: 'Applicable Standard Name',
		baseFrom: (_baseKey, baseProp) => {
			const parsed = getParsedLeafDesc(baseProp);
			return parsed ? parsed.applicableStandardName : '';
		},
	},
	{
		key: 'dataFormat',
		label: 'Data Format',
		baseFrom: (_baseKey, baseProp) => {
			const parsed = getParsedLeafDesc(baseProp);
			return parsed ? parsed.dataFormat : '';
		},
	},
	{
		key: 'exampleValue',
		label: 'Example Value',
		// Mirror extractSchemaFields (index.js:132-138): prefer singular
		// `example` first, then plural `examples[0]`.
		baseFrom: (_baseKey, baseProp) => {
			if (Object.prototype.hasOwnProperty.call(baseProp || {}, 'example')) {
				return baseProp.example ?? null;
			}
			if (Array.isArray(baseProp?.examples) && baseProp.examples.length > 0) {
				return baseProp.examples[0] ?? null;
			}
			return null;
		},
		equals: exampleEquals,
		format: formatExample,
	},
];

function compareDescriptorValues(descriptor, baseValue, userValue) {
	const normalize = descriptor.normalize || defaultNormalize;
	const equals = descriptor.equals || defaultEquals;
	const na = normalize(baseValue);
	const nb = normalize(userValue);
	if (isNullish(na) && isNullish(nb)) return true;
	if (isNullish(na) || isNullish(nb)) return false;
	return equals(na, nb);
}

function getFieldChanges(field, baseKey, baseProp, baseRequired, parentPath) {
	const changes = [];

	// Technical Field Name renames are compared against the lookup key, not baseProp.
	if (field.dataElementName !== baseKey) {
		changes.push({ property: 'Technical Field Name', before: baseKey, after: field.dataElementName });
	}

	for (const descriptor of CONSTRAINT_PROPS) {
		const baseValue = descriptor.baseFrom(baseKey, baseProp, baseRequired, parentPath);
		const userValue = field[descriptor.key];
		if (compareDescriptorValues(descriptor, baseValue, userValue)) continue;
		const format = descriptor.format || formatScalar;
		changes.push({
			property: descriptor.label,
			before: format(baseValue),
			after: format(userValue),
		});
	}

	return changes;
}

function getChildBaseState(baseProp) {
	if (baseProp?.properties) {
		return { props: baseProp.properties, required: baseProp.required || [] };
	}
	if (baseProp?.items?.properties) {
		return { props: baseProp.items.properties, required: baseProp.items.required || [] };
	}
	return { props: {}, required: [] };
}

function appendRemovedFields(baseProps, matchedBaseKeys, parentPath, removed) {
	Object.keys(baseProps).forEach(key => {
		if (matchedBaseKeys.has(key)) return;
		const baseProp = baseProps[key];
		const displayPath = parentPath ? `${parentPath}.${key}` : key;
		removed.push({ dataElementName: key, displayPath, dataType: baseProp?.type });
		// Symmetric with collectDescendantFields (added side): enumerate the
		// descendants of a removed object / array-of-object so removed and
		// added subtrees report the same number of rows.
		const childState = getChildBaseState(baseProp);
		if (Object.keys(childState.props).length > 0) {
			appendRemovedFields(childState.props, new Set(), displayPath, removed);
		}
	});
}

export function diffAgainstBase(localFields, baseSchema) {
	const added = [];
	const modified = [];
	const removed = [];

	function diffLevel(fields, baseProps = {}, baseRequired = [], parentPath = '') {
		const matchedBaseKeys = new Set();

		sortFieldsAttachmentsLast(fields).forEach(field => {
			if (field.status === 'Deleted') return;

			const displayPath = parentPath ? `${parentPath}.${field.dataElementName}` : field.dataElementName;
			const { baseKey, baseProp } = getBaseContext(field, baseProps, parentPath);

			if (!baseProp) {
				added.push({ ...field, displayPath });
				collectDescendantFields(field, displayPath, added);
				return;
			}

			matchedBaseKeys.add(baseKey);
			const changes = getFieldChanges(field, baseKey, baseProp, baseRequired, parentPath);
			if (changes.length > 0) {
				modified.push({ field: { ...field, displayPath }, changes });
			}

			const activeChildren = getActiveChildren(field);
			const childBaseState = getChildBaseState(baseProp);
			// Always descend when either side has children: UI-side children
			// produce added/modified entries; empty UI + non-empty base produces
			// removed entries (matters when the user deletes the sole child).
			if (activeChildren.length > 0 || Object.keys(childBaseState.props).length > 0) {
				diffLevel(activeChildren, childBaseState.props, childBaseState.required, displayPath);
			}
		});

		appendRemovedFields(baseProps, matchedBaseKeys, parentPath, removed);
	}

	diffLevel(localFields, baseSchema?.properties || {}, baseSchema?.required || [], '');
	return { added, modified, removed };
}

/**
 * Compare current rules (UI state) against the baseline parsed from the
 * persisted schema. Rules can live under `x-allOf` (UI authoring marker) or
 * plain `allOf` (DB canonical form) — `extractRulesFromSchema` merges both
 * sources. Rules are keyed by `id`; new rules (UUID ids) that don't exist in
 * the baseline surface as `added`, deleted ones as `removed`, and
 * content-changed ones as `modified`. Equality uses `canonicalize(emitRule)`
 * so reordering condition siblings or property keys does not register as a
 * change.
 */
/**
 * Compute added / modified / removed entries for the root-level metadata
 * keywords the editor surfaces but `diffAgainstBase` ignores (it walks
 * `properties` only).
 *
 * Returns shape:
 *   {
 *     added:    [{ key, after }],
 *     modified: [{ key, before, after }],
 *     removed:  [{ key, before }],
 *   }
 *
 * Values are pre-formatted display strings (not raw values) so the modal
 * renderer can drop them straight into a table. JSON Schema's
 * `additionalProperties` is the headline case — the toggle deliberately
 * emits the keyword only when restricting, so detecting "key omitted ⇒
 * permissive" requires comparing presence too, not just values.
 */
export function diffRootMetadata(emittedSchema, baseSchema) {
	const TRACKED_KEYS = [
		'title',
		'description',
		'$id',
		'additionalProperties',
		'minProperties',
		'maxProperties',
		'securityClassification',
		'sensitivityClassification',
		'anyOf',
		'oneOf',
	];

	const added = [];
	const modified = [];
	const removed = [];

	if (!emittedSchema || typeof emittedSchema !== 'object') {
		return { added, modified, removed };
	}

	const safeBase = (baseSchema && typeof baseSchema === 'object') ? baseSchema : {};

	for (const key of TRACKED_KEYS) {
		const inEmitted = Object.prototype.hasOwnProperty.call(emittedSchema, key);
		const inBase = Object.prototype.hasOwnProperty.call(safeBase, key);

		if (!inEmitted && !inBase) continue;

		const emittedVal = inEmitted ? emittedSchema[key] : undefined;
		const baseVal = inBase ? safeBase[key] : undefined;

		if (inEmitted && !inBase) {
			added.push({ key, after: formatRootMetadataValue(emittedVal) });
		} else if (!inEmitted && inBase) {
			removed.push({ key, before: formatRootMetadataValue(baseVal) });
		} else if (!areJsonEqual(emittedVal, baseVal)) {
			modified.push({
				key,
				before: formatRootMetadataValue(baseVal),
				after: formatRootMetadataValue(emittedVal),
			});
		}
	}

	return { added, modified, removed };
}

const CUSTOM_VALIDATOR_LABELS = {
	masterDataMappings: 'Master Data Mappings',
	dateCompare: 'Date Comparison Rule',
	sameDay: 'Same Day Rule',
	maxTotalDuration: 'Max Total Duration Rule',
	sumEquals: 'Sum Equals Rule',
};

/**
 * Compute added / modified / removed entries for the `x-customValidators`
 * editor record. Mirrors `diffRootMetadata` shape so the modal can render
 * via the same table template.
 *
 * Returns:
 *   {
 *     added:    [{ key, after }],
 *     modified: [{ key, before, after }],
 *     removed:  [{ key, before }],
 *   }
 *
 * Keys for the five named validators are friendly labels; keys for
 * `additional` entries use the raw user-provided key prefixed with the
 * literal "additional:" so the modal can show e.g. "additional:foo".
 */
export function diffCustomValidators(emittedSchema, baseSchema) {
	const emitted = extractCustomValidators(emittedSchema || {});
	const base = extractCustomValidators(baseSchema || {});

	const added = [];
	const modified = [];
	const removed = [];

	for (const k of CUSTOM_VALIDATOR_KEYS) {
		const emitVal = emitted[k];
		const baseVal = base[k];

		if (emitVal === null && baseVal === null) continue;

		if (emitVal !== null && baseVal === null) {
			added.push({ key: CUSTOM_VALIDATOR_LABELS[k], after: formatRootMetadataValue(emitVal) });
		} else if (emitVal === null && baseVal !== null) {
			removed.push({ key: CUSTOM_VALIDATOR_LABELS[k], before: formatRootMetadataValue(baseVal) });
		} else if (!areJsonEqual(emitVal, baseVal)) {
			modified.push({
				key: CUSTOM_VALIDATOR_LABELS[k],
				before: formatRootMetadataValue(baseVal),
				after: formatRootMetadataValue(emitVal),
			});
		}
	}

	const emitAdditionalMap = new Map(
		Array.isArray(emitted.additional)
			? emitted.additional.map(({ key, value }) => [key, value])
			: [],
	);
	const baseAdditionalMap = new Map(
		Array.isArray(base.additional)
			? base.additional.map(({ key, value }) => [key, value])
			: [],
	);

	const allAdditionalKeys = new Set([...emitAdditionalMap.keys(), ...baseAdditionalMap.keys()]);
	for (const rawKey of allAdditionalKeys) {
		const inEmit = emitAdditionalMap.has(rawKey);
		const inBase = baseAdditionalMap.has(rawKey);
		const rowKey = `additional:${rawKey}`;

		if (inEmit && !inBase) {
			added.push({ key: rowKey, after: formatRootMetadataValue(emitAdditionalMap.get(rawKey)) });
		} else if (!inEmit && inBase) {
			removed.push({ key: rowKey, before: formatRootMetadataValue(baseAdditionalMap.get(rawKey)) });
		} else if (!areJsonEqual(emitAdditionalMap.get(rawKey), baseAdditionalMap.get(rawKey))) {
			modified.push({
				key: rowKey,
				before: formatRootMetadataValue(baseAdditionalMap.get(rawKey)),
				after: formatRootMetadataValue(emitAdditionalMap.get(rawKey)),
			});
		}
	}

	return { added, modified, removed };
}

/**
 * Compare the *ordering* of root-level `properties` keys and `required[]`
 * entries between emitted and base schemas. JSON Schema treats both as
 * unordered, but downstream form renderers display fields in declared
 * order — a reorder is a real UX change a reviewer should see.
 *
 * Returns null when both orderings match (no UI row needed), otherwise:
 *   {
 *     properties: null | { before: string[], after: string[] },
 *     required:   null | { before: string[], after: string[] },
 *   }
 *
 * Whole-array snapshots, not per-element edit distance. Membership
 * deltas are surfaced by `diffAgainstBase` (Field Changes); this helper
 * intentionally shows the full new ordering for reviewer context.
 *
 * Root-level only — nested object reorders are out of scope (mirrors the
 * Field Changes / Root Metadata Changes scoping).
 */
export function diffFieldOrder(emittedSchema, baseSchema) {
	const safeEmit = emittedSchema || {};
	const safeBase = baseSchema || {};

	const propsBefore = Object.keys(safeBase.properties || {});
	const propsAfter = Object.keys(safeEmit.properties || {});

	const reqBefore = Array.isArray(safeBase.required) ? safeBase.required : [];
	const reqAfter = Array.isArray(safeEmit.required) ? safeEmit.required : [];

	const propertiesSlot = areJsonEqual(propsBefore, propsAfter)
		? null
		: { before: [...propsBefore], after: [...propsAfter] };

	const requiredSlot = areJsonEqual(reqBefore, reqAfter)
		? null
		: { before: [...reqBefore], after: [...reqAfter] };

	if (propertiesSlot === null && requiredSlot === null) return null;

	return { properties: propertiesSlot, required: requiredSlot };
}

function formatRootMetadataValue(value) {
	if (value === undefined || value === null) return '(not set)';
	if (typeof value === 'boolean') return String(value);
	if (typeof value === 'number') return String(value);
	if (typeof value === 'string') {
		const trimmed = value.length > 80 ? `${value.slice(0, 80)}…` : value;
		return trimmed || '(empty)';
	}
	if (Array.isArray(value)) return `(${value.length} entr${value.length === 1 ? 'y' : 'ies'})`;
	try {
		return JSON.stringify(value);
	} catch {
		return '(non-serialisable)';
	}
}

function areJsonEqual(a, b) {
	try {
		return JSON.stringify(canonicalize(a)) === JSON.stringify(canonicalize(b));
	} catch {
		return a === b;
	}
}

export function diffXAllOfRules(currentRules, baseSchema) {
	const safeCurrent = Array.isArray(currentRules) ? currentRules : [];
	const { rules: baseRules } = extractRulesFromSchema(baseSchema);

	const baseById = new Map(baseRules.map(r => [r.id, r]));
	const currentById = new Map(safeCurrent.map(r => [r.id, r]));

	// JSON Schema treats `required` as an unordered set — sort it recursively
	// so a pure reorder of `if.required` (e.g. via sibling-condition reorder)
	// does not surface as "modified".
	const sortRequiredArrays = (value) => {
		if (Array.isArray(value)) return value.map(sortRequiredArrays);
		if (value && typeof value === 'object') {
			const out = {};
			for (const k of Object.keys(value)) {
				out[k] = (k === 'required' && Array.isArray(value[k]))
					? [...value[k]].sort()
					: sortRequiredArrays(value[k]);
			}
			return out;
		}
		return value;
	};

	const canonical = (rule) => {
		const emitted = emitRule(rule);
		if (emitted == null) return '';
		return JSON.stringify(canonicalize(sortRequiredArrays(emitted)));
	};

	const added = safeCurrent.filter(r => !baseById.has(r.id));
	const removed = baseRules.filter(r => !currentById.has(r.id));
	const modified = safeCurrent
		.filter(r => baseById.has(r.id))
		.map(r => ({ baseRule: baseById.get(r.id), rule: r }))
		.filter(({ baseRule, rule }) => canonical(baseRule) !== canonical(rule));

	return { added, modified, removed };
}

/**
 * JSON Schema draft-2020-12 only accepts the seven core types. The admin-ui
 * DATA_TYPES catalog exposes `date` and `date-time` as ergonomic shorthands;
 * lower them to `{ type: 'string', format: '<date|date-time>' }` at emission
 * time so downstream validators (ajv, santhosh-tekuri/jsonschema) don't
 * reject the schema outright.
 */
function lowerDateLikeType(dataType) {
	if (dataType === 'date') return { type: 'string', format: 'date' };
	if (dataType === 'date-time') return { type: 'string', format: 'date-time' };
	if (dataType === 'custom:date-time:YYYY-MM') return { type: 'string', format: 'custom:date-time:YYYY-MM' };
	return null;
}

function coerceEnumValues(values, dataType) {
	if (dataType === 'integer') {
		return values.map(v => {
			const n = Number.parseInt(v, 10);
			return Number.isNaN(n) ? v : n;
		});
	}
	if (dataType === 'number') {
		return values.map(v => {
			const n = Number.parseFloat(v);
			return Number.isNaN(n) ? v : n;
		});
	}
	if (dataType === 'boolean') {
		return values.map(v => {
			if (v === 'true' || v === true) return true;
			if (v === 'false' || v === false) return false;
			return v;
		});
	}
	return values;
}

function buildBaseFieldSchema(field, originalProp) {
	const base = originalProp ? { ...originalProp } : {};

	// Lower date/date-time to standard JSON Schema types before any
	// user-supplied `format` override kicks in.
	const lowered = lowerDateLikeType(field.dataType);
	if (lowered) {
		base.type = lowered.type;
		base.format = lowered.format;
	} else {
		base.type = field.dataType || 'string';
	}

	// Re-wrap as a 2-element array when the source authored `type: [<t>, "null"]`.
	// extractSchemaFields stashed this in `field.nullable`. Without this,
	// the rebuilt schema would reject nulls the source accepted (functional
	// regression).
	if (field.nullable === true) {
		base.type = [base.type, 'null'];
	}

	// Suppress synthetic `type` when the source authored a typeless schema
	// (e.g. `{ enum: [1, "1", true, null] }` — valid JSON Schema, accepts
	// the union of the enum's types). resolveSchemaFieldType falls back to
	// 'string' which would narrow the rebuilt schema's accept set. Fresh
	// UI fields leave `typeExplicit === undefined` so they still emit type.
	// Only `typeExplicit === false` (set by extractSchemaFields when source
	// explicitly omitted `type`) suppresses emit. Companion change in
	// validateSchemaShape allows typeless fields when they carry `enum`
	// or `const`, so the Confirm modal still opens.
	if (field.typeExplicit === false && field.nullable !== true) {
		delete base.type;
	}

	// Suppress synthetic title when the source schema had no `title` keyword
	// (B6). formatSchemaBusinessTerm hardcodes `attachments → "Attachments"`,
	// which would otherwise inject a title where source had none. Fresh UI
	// fields have `titleExplicit === undefined` so they still emit (regression-
	// safe). Only `titleExplicit === false` (set by extractSchemaFields when
	// source explicitly omitted title) suppresses emit.
	if (field.titleExplicit === false) {
		delete base.title;
	} else {
		base.title = field.businessTerm || field.dataElementName;
	}

	// Description handling. The five sub-fields each become one labeled
	// `<div>` in a `<root>...</root>` blob. We special-case the "user
	// didn't edit" path so byte-for-byte round-trip survives even when
	// the production blob's `businessTerm` disagrees with `title` — a
	// real inconsistency in admin_buildex_dev that would otherwise be
	// silently overwritten on save.
	const hasStructuredDescription = field.descriptionIsStructured === true
		|| field.businessDefinition
		|| field.applicableStandardName
		|| field.dataFormat;
	if (hasStructuredDescription) {
		// Short-circuit: re-emit the original blob verbatim when the
		// editor's five fields haven't drifted from what we parsed out
		// of it. Comparison is on parsed values (not raw strings) so
		// stripping/normalizing of internal whitespace by the parser
		// doesn't cause a spurious re-emit.
		let usedOriginal = false;
		if (typeof field.originalDescriptionBlob === 'string'
			&& isLeafDescriptionBlob(field.originalDescriptionBlob)) {
			const parsedOriginal = parseLeafDescription(field.originalDescriptionBlob);
			if (parsedOriginal
				&& parsedOriginal.description === (field.description ?? '')
				&& parsedOriginal.businessDefinition === (field.businessDefinition ?? '')
				&& parsedOriginal.applicableStandardName === (field.applicableStandardName ?? '')
				&& parsedOriginal.dataFormat === (field.dataFormat ?? '')) {
				base.description = field.originalDescriptionBlob;
				usedOriginal = true;
			}
		}
		if (!usedOriginal) {
			const serialized = serializeLeafDescription({
				description: field.description ?? '',
				businessTerm: field.businessTerm ?? field.dataElementName ?? '',
				businessDefinition: field.businessDefinition ?? '',
				applicableStandardName: field.applicableStandardName ?? '',
				dataFormat: field.dataFormat ?? '',
			});
			if (serialized) {
				base.description = serialized;
			} else {
				delete base.description;
			}
		}
	} else if (field.description) {
		base.description = field.description;
	} else if (field.descriptionExplicit) {
		// The source schema had an explicit (possibly empty) description —
		// preserve it instead of deleting on round-trip.
		base.description = field.description ?? '';
	} else {
		delete base.description;
	}

	if (field.format) {
		base.format = field.format;
	}
	// Round-trip the source's chosen example keyword. Both `example`
	// (singular, draft-7) and `examples` (plural, 2020-12) appear in
	// production schemas; normalizing one to the other breaks byte-equal
	// fidelity. exampleKeyword is captured at extraction; fresh UI fields
	// leave it null and default to plural.
	if (hasExampleValue(field.exampleValue)) {
		if (field.exampleKeyword === 'example') {
			base.example = field.exampleValue;
			delete base.examples;
		} else {
			base.examples = [field.exampleValue];
			delete base.example;
		}
	} else {
		delete base.examples;
		delete base.example;
	}

	// Validation constraints — only emit when non-null/non-empty
	if (field.minLength != null && field.minLength !== '') {
		base.minLength = Number.parseInt(field.minLength, 10);
	} else {
		delete base.minLength;
	}
	if (field.maxLength != null && field.maxLength !== '') {
		base.maxLength = Number.parseInt(field.maxLength, 10);
	} else {
		delete base.maxLength;
	}
	if (field.minimum != null && field.minimum !== '') {
		base.minimum = parseFloat(field.minimum);
	} else {
		delete base.minimum;
	}
	if (field.maximum != null && field.maximum !== '') {
		base.maximum = parseFloat(field.maximum);
	} else {
		delete base.maximum;
	}
	if (field.exclusiveMinimum != null && field.exclusiveMinimum !== '') {
		base.exclusiveMinimum = parseFloat(field.exclusiveMinimum);
	} else {
		delete base.exclusiveMinimum;
	}
	if (field.exclusiveMaximum != null && field.exclusiveMaximum !== '') {
		base.exclusiveMaximum = parseFloat(field.exclusiveMaximum);
	} else {
		delete base.exclusiveMaximum;
	}
	if (field.pattern) {
		base.pattern = field.pattern;
	} else {
		delete base.pattern;
	}
	if (field.enumValues && Array.isArray(field.enumValues) && field.enumValues.length > 0) {
		// enumValues are always stored as strings (parsed from the comma-
		// separated text input). Coerce to the field's declared type so
		// integer/number/boolean enums validate correctly downstream.
		base.enum = coerceEnumValues(field.enumValues, field.dataType);
	} else {
		delete base.enum;
	}
	if (field.multipleOf != null && field.multipleOf !== '') {
		base.multipleOf = parseFloat(field.multipleOf);
	} else {
		delete base.multipleOf;
	}
	if (field.minItems != null && field.minItems !== '') {
		base.minItems = Number.parseInt(field.minItems, 10);
	} else {
		delete base.minItems;
	}
	if (field.maxItems != null && field.maxItems !== '') {
		base.maxItems = Number.parseInt(field.maxItems, 10);
	} else {
		delete base.maxItems;
	}
	if (field.uniqueItems === true) {
		base.uniqueItems = true;
	} else if (field.uniqueItems === false && field.uniqueItemsExplicit) {
		// Preserve an explicitly authored `uniqueItems: false` (GAP-5).
		// Fresh UI fields leave `uniqueItemsExplicit` undefined so their
		// default `false` does not pollute the emitted schema.
		base.uniqueItems = false;
	} else {
		delete base.uniqueItems;
	}

	// Object property-count constraints (minProperties / maxProperties).
	// Captured by extractSchemaFields for every object field, including
	// propertyless ones (e.g. `{type:object, minProperties:2,
	// additionalProperties:true}`) which fall through buildFieldSchema to
	// this leaf path. Emitting here means objects-WITH-children also get
	// them for free via buildObjectFieldSchema's `{ ...base }` spread. The
	// builder has no editor surface for these; without re-emit they drop.
	if (field.dataType === 'object' && field.minProperties != null && field.minProperties !== '') {
		base.minProperties = Number.parseInt(field.minProperties, 10);
	} else {
		delete base.minProperties;
	}
	if (field.dataType === 'object' && field.maxProperties != null && field.maxProperties !== '') {
		base.maxProperties = Number.parseInt(field.maxProperties, 10);
	} else {
		delete base.maxProperties;
	}
	// Propertyless objects (no children) never reach buildObjectFieldSchema,
	// so re-emit their `additionalProperties` here. Objects WITH children
	// have it set authoritatively in buildObjectFieldSchema; arrays manage
	// it at the array/items level — so gate strictly on object type and let
	// buildObjectFieldSchema's later set win for the with-children case via
	// its `{ ...base }` spread + explicit assignment.
	if (field.dataType === 'object' && typeof field.additionalProperties === 'boolean') {
		base.additionalProperties = field.additionalProperties;
	}

	// Re-emit `const` when the source authored it. Captured opaquely by
	// extractSchemaFields (no UI surface for editing). Without this, every
	// primitive const declaration silently disappears on round-trip.
	if (field.constExplicit) {
		base.const = field.constValue;
	} else {
		delete base.const;
	}

	// x-uitype / x-uioptions: round-trip preservation. The hook captures
	// these as opaque values; the emitter writes them back exactly as
	// captured unless the user has overridden them in the field modal.
	if (typeof field.xUitype === 'string' && field.xUitype !== '') {
		base['x-uitype'] = field.xUitype;
	} else if (field.xUitype === null && Object.prototype.hasOwnProperty.call(base, 'x-uitype')) {
		delete base['x-uitype'];
	}
	if (field.xUioptions !== undefined) {
		base['x-uioptions'] = field.xUioptions;
	} else if (Object.prototype.hasOwnProperty.call(base, 'x-uioptions')) {
		delete base['x-uioptions'];
	}

	return base;
}

// Re-sort an emitted `required` array to follow source's authoring order
// (B3). The emitter assembles required[] in properties-iteration order, but
// JSON Schema permits arbitrary required[] order independent of properties.
// Source order first; any required entries not present in source order
// (e.g. user just toggled a field to mandatory in the editor) get appended.
export function reorderRequired(required, requiredOrder) {
	if (!Array.isArray(requiredOrder) || !Array.isArray(required) || required.length === 0) {
		return required;
	}
	const have = new Set(required);
	const seen = new Set();
	const ordered = [];
	for (const name of requiredOrder) {
		if (have.has(name) && !seen.has(name)) {
			ordered.push(name);
			seen.add(name);
		}
	}
	for (const name of required) {
		if (!seen.has(name)) {
			ordered.push(name);
			seen.add(name);
		}
	}
	return ordered;
}

function buildObjectFieldSchema(base, field, activeChildren, originalProp, buildFieldSchemaFn) {
	const properties = {};
	const required = [];

	sortFieldsAttachmentsLast(activeChildren).forEach(child => {
		const childKey = child.dataElementName;
		properties[childKey] = buildFieldSchemaFn(child, originalProp?.properties?.[childKey]);
		if (child.mandatory) required.push(childKey);
	});

	const out = { ...base, properties };
	const orderedRequired = reorderRequired(required, field?.requiredOrder);
	if (orderedRequired.length > 0) out.required = orderedRequired;
	else delete out.required;
	// Re-emit `additionalProperties` when the source authored it. Captured
	// at extraction so create-mode (no baseSchema to spread from) works too.
	if (typeof field?.additionalProperties === 'boolean') {
		out.additionalProperties = field.additionalProperties;
	}
	// Re-emit nested conditional rules. The XAllOfBuilder edits only root-
	// level rules; rules attached to sub-objects have no editor surface, so
	// the extractor stashes them on the field and we round-trip them here.
	//
	// Conditional rules must SAVE under `x-allOf` (matching the root-level
	// builder): admin-core's shape validator requires every `x-allOf` entry
	// to be a `{if, then?, else?}` object, and the Go producer lowers
	// `x-allOf`→`allOf` before validating. splitNestedAllOf moves genuine
	// conditional entries (merged with `nestedXAllOf`) to `x-allOf` and keeps
	// residual non-conditional composition (`{anyOf}`, `{$ref}`) under `allOf`.
	return splitNestedAllOf(out, field?.nestedAllOf, field?.nestedXAllOf);
}

function buildArrayFieldSchema(base, field, activeChildren, originalProp, buildFieldSchemaFn) {
	const itemType = field.arrayItemType || 'object';

	if (itemType === 'object' && activeChildren.length > 0) {
		const itemProps = {};
		const itemRequired = [];
		sortFieldsAttachmentsLast(activeChildren).forEach(child => {
			const childKey = child.dataElementName;
			itemProps[childKey] = buildFieldSchemaFn(child, originalProp?.items?.properties?.[childKey]);
			if (child.mandatory) itemRequired.push(childKey);
		});
		// Spread originalProp.items first (update mode) so item-level keywords
		// round-trip through the editor; spread itemsRawMeta after to cover
		// create mode (where originalProp is undefined). itemsRawMeta is the
		// extractor's verbatim copy of `value.items` minus the four keys the
		// builder authors below (B2).
		const items = {
			...(originalProp?.items || {}),
			...(field.itemsRawMeta || {}),
			type: 'object',
			properties: itemProps,
		};
		const orderedItemRequired = reorderRequired(itemRequired, field?.requiredOrder);
		if (orderedItemRequired.length > 0) items.required = orderedItemRequired;
		else delete items.required;
		// Items-level additionalProperties (extracted from `value.items.
		// additionalProperties`). Separate slot from the array-level value
		// captured below (B1).
		if (typeof field.itemsAdditionalProperties === 'boolean') {
			items.additionalProperties = field.itemsAdditionalProperties;
		}
		// Array<object> items can also carry captured conditional rules —
		// they survive extraction inside `itemsRawMeta.allOf` (create mode)
		// or `originalProp.items.allOf` (update mode) and land on `items`
		// via the spreads above. Apply the same conditional→x-allOf split
		// here so item-level rules save under `x-allOf`, keeping residual
		// composition under plain `allOf`.
		const splitItems = splitNestedAllOf(items, items.allOf, items['x-allOf']);
		const out = { ...base, items: splitItems };
		// Array-level additionalProperties (extracted from `value.
		// additionalProperties` on the array field itself, as a sibling of
		// `items`). Production attachments arrays carry it (B1).
		if (typeof field.additionalProperties === 'boolean') {
			out.additionalProperties = field.additionalProperties;
		}
		return out;
	}

	// Array-of-array: when extractSchemaFields stashed the inner items subtree
	// (UI has no editor surface for nested arrays), re-emit it verbatim so
	// nested type / minItems / uniqueItems survive the round-trip.
	if (itemType === 'array' && field.innerArraySchema) {
		return { ...base, items: field.innerArraySchema };
	}

	if (itemType === 'array' && activeChildren.length === 1) {
		return { ...base, items: buildFieldSchemaFn(activeChildren[0], originalProp?.items) };
	}

	// Primitive-typed array: spread originalProp.items (update mode) and
	// itemsRawMeta (create mode) so item-level keywords like `enum`,
	// `minLength`, `pattern`, `format` round-trip. The base `{ type }` is
	// authored last so the builder owns the type slot. Mirrors the object-
	// items branch above.
	const loweredItems = lowerDateLikeType(itemType);
	const baseItems = loweredItems || { type: itemType };
	return {
		...base,
		items: {
			...(originalProp?.items || {}),
			...(field.itemsRawMeta || {}),
			...baseItems,
		},
	};
}

export function buildFieldSchema(field, originalProp) {
	const base = buildBaseFieldSchema(field, originalProp);
	const activeChildren = getActiveChildren(field);

	if (field.dataType === 'object' && activeChildren.length > 0) {
		return buildObjectFieldSchema(base, field, activeChildren, originalProp, buildFieldSchema);
	}

	if (field.dataType === 'array') {
		return buildArrayFieldSchema(base, field, activeChildren, originalProp, buildFieldSchema);
	}

	return base;
}

function isConditionalAllOfEntry(entry) {
	if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) return false;
	const keys = Object.keys(entry);
	if (!keys.includes('if')) return false;
	if (!keys.includes('then') && !keys.includes('else')) return false;
	return keys.every((k) => k === 'if' || k === 'then' || k === 'else');
}

// Split captured nested composition into the two save buckets. Conditional
// `{if, then?, else?}` entries (from either source) merge under `x-allOf`
// (admin-core's shape validator requires every `x-allOf` entry to be
// conditional; the Go producer lowers `x-allOf`→`allOf` before validating).
// Residual non-conditional composition (`{anyOf}`, `{$ref}`, ...) stays under
// plain `allOf`. Returns a new object with the buckets applied; empty buckets
// are omitted entirely (no `allOf:[]` / `x-allOf:[]`).
function splitNestedAllOf(target, sourceAllOf, sourceXAllOf) {
	const allOf = Array.isArray(sourceAllOf) ? sourceAllOf : [];
	const xAllOf = Array.isArray(sourceXAllOf) ? sourceXAllOf : [];
	const conditionalFromAllOf = allOf.filter(isConditionalAllOfEntry);
	const residualAllOf = allOf.filter((entry) => !isConditionalAllOfEntry(entry));
	const finalXAllOf = [...xAllOf, ...conditionalFromAllOf];

	const out = { ...target };
	if (finalXAllOf.length > 0) out['x-allOf'] = finalXAllOf;
	else delete out['x-allOf'];
	if (residualAllOf.length > 0) out.allOf = residualAllOf;
	else delete out.allOf;
	return out;
}

export function buildJsonSchema(fields, baseSchema, xAllOfRules, allOfRules) {
	const schema = baseSchema ? { ...baseSchema } : { type: 'object', properties: {} };
	const originalProperties = baseSchema?.properties || {};

	schema.properties = {};
	const required = [];

	sortFieldsAttachmentsLast(fields).forEach(field => {
		if (field.status === 'Deleted') return;
		schema.properties[field.dataElementName] = buildFieldSchema(field, originalProperties[field.dataElementName]);
		if (field.mandatory) required.push(field.dataElementName);
	});

	if (required.length > 0) schema.required = required;
	else delete schema.required;

	// Add x-allOf at root if rules exist
	if (xAllOfRules && Array.isArray(xAllOfRules) && xAllOfRules.length > 0) {
		schema['x-allOf'] = xAllOfRules;
	} else {
		delete schema['x-allOf'];
	}

	// Plain `allOf`: preserve any non-rule pass-through entries from
	// baseSchema (e.g. `{anyOf: ...}` blocks) and append rule entries the
	// editor manages. The editor identifies "rule entries" as objects with
	// only `{if, then?, else?}` keys — anything else flows through verbatim.
	const baseAllOf = Array.isArray(baseSchema?.allOf) ? baseSchema.allOf : [];
	const residualAllOf = baseAllOf.filter((entry) => !isConditionalAllOfEntry(entry));
	const newAllOfRules = Array.isArray(allOfRules) ? allOfRules : [];
	const finalAllOf = [...residualAllOf, ...newAllOfRules];
	if (finalAllOf.length > 0) {
		schema.allOf = finalAllOf;
	} else {
		delete schema.allOf;
	}

	return schema;
}

/**
 * Convenience wrapper for callers that hold rules-state (with `sourceKey`
 * stamps from extractRulesFromSchema) and want a finished schema. Splits
 * rules by their source keyword, emits each bucket, and forwards into
 * buildJsonSchema. Tests and code that already work in the pre-emitted
 * shape can continue to call buildJsonSchema directly.
 */
export function buildJsonSchemaFromRules(fields, baseSchema, rules) {
	const { xAllOf, allOf } = emitRulesByKeyword(rules);
	return buildJsonSchema(
		fields,
		baseSchema,
		xAllOf.length > 0 ? xAllOf : null,
		allOf,
	);
}

const QUERY_SCHEMA_SCALAR_TYPES = new Set(['string', 'number', 'integer', 'boolean']);

function isScalarQuerySchemaPropertyDef(propDef) {
	if (!propDef || typeof propDef !== 'object') return false;
	const t = propDef.type;
	if (typeof t === 'string') return QUERY_SCHEMA_SCALAR_TYPES.has(t);
	if (Array.isArray(t)) return t.every(x => x === 'null' || QUERY_SCHEMA_SCALAR_TYPES.has(x));
	return false;
}

/**
 * Derive a querySchema from an elementSchema by mirroring the scalar subset of its
 * top-level `required` list. Property definitions are copied verbatim. The resulting
 * querySchema acts as the pull-side AJV contract — `properties` is the filter
 * whitelist / signature-index key-set, and `required` is the mandatory query params.
 *
 * Returns `{}` when the elementSchema has no scalar required field; the caller is
 * expected to treat that as "cannot create" (see the hard-constraint gate in
 * dsv-update-schema.jsx).
 *
 * @param {object} elementSchema
 * @returns {object}
 */
export function buildQuerySchemaFromElementSchema(elementSchema) {
	if (!elementSchema || typeof elementSchema !== 'object') return {};
	const { properties, required } = elementSchema;
	if (!properties || typeof properties !== 'object') return {};
	if (!Array.isArray(required) || required.length === 0) return {};

	const scalarRequired = required.filter(name => isScalarQuerySchemaPropertyDef(properties[name]));
	if (scalarRequired.length === 0) return {};

	const queryProperties = {};
	scalarRequired.forEach(name => {
		queryProperties[name] = JSON.parse(JSON.stringify(properties[name]));
	});

	return {
		type: 'object',
		additionalProperties: false,
		properties: queryProperties,
		required: scalarRequired,
	};
}

function walkAndTransform(node) {
	if (!node || typeof node !== 'object' || Array.isArray(node)) return;

	if (Array.isArray(node['x-allOf'])) {
		if (Array.isArray(node.allOf)) {
			node.allOf = node.allOf.concat(node['x-allOf']);
		} else {
			node.allOf = node['x-allOf'];
		}
		delete node['x-allOf'];
	}

	// Recurse into properties, items, additionalProperties, allOf/anyOf/oneOf, if/then/else
	if (node.properties) {
		Object.values(node.properties).forEach(walkAndTransform);
	}
	if (node.items && typeof node.items === 'object') walkAndTransform(node.items);
	if (node.additionalProperties && typeof node.additionalProperties === 'object') walkAndTransform(node.additionalProperties);
	['allOf', 'anyOf', 'oneOf'].forEach(kw => {
		if (Array.isArray(node[kw])) node[kw].forEach(walkAndTransform);
	});
	['if', 'then', 'else'].forEach(kw => {
		if (node[kw] && typeof node[kw] === 'object') walkAndTransform(node[kw]);
	});
}

/**
 * Transform x-allOf to standard allOf recursively (browser-side equivalent of Go preprocessor).
 * Deep-clones the input so the original is not mutated.
 * @param {object} schema - JSON Schema that may contain x-allOf keywords
 * @returns {object} A new schema with x-allOf replaced by allOf
 */
export function preprocessXAllOf(schema) {
	if (!schema || typeof schema !== 'object') return schema;
	const result = JSON.parse(JSON.stringify(schema)); // deep clone
	walkAndTransform(result);
	return result;
}

// ============ x-allOf rule validation ============

const OPERATOR_NEEDS_VALUE = new Set([
	'const', 'enum', 'not_in',
	'minimum', 'maximum', 'exclusive_minimum', 'exclusive_maximum',
	'contains_const',
]);

function isRegexValid(pattern) {
	try {
		// eslint-disable-next-line no-new
		new RegExp(pattern);
		return true;
	} catch {
		return false;
	}
}

function addError(bucket, key, path, message) {
	if (!bucket[key]) bucket[key] = {};
	if (!bucket[key][path]) bucket[key][path] = [];
	bucket[key][path].push(message);
}

/**
 * A bound value is "present" only when it is neither `null`/`undefined` nor an
 * empty string. UI inputs surface cleared fields as `''`, persisted schemas as
 * `null`; both mean "no constraint" and must never trip a comparison.
 *
 * @param {*} v
 * @returns {boolean}
 */
function isBoundPresent(v) {
	return v !== '' && v != null;
}

// ISO-8601 length facts used by the date / date-time sanity checks below.
// A `date` value (`YYYY-MM-DD`) is exactly 10 chars. A `date-time` value runs
// 20–25 chars depending on the fractional-seconds / timezone suffix. Length
// limits that exclude these ranges make json-schema-faker throw
// `RangeError: Invalid time value` when generating a sample template, so we
// surface them as blocking errors instead of an opaque server 400.
// Jul 2026: Agreed that buildex data elements must use exactly 25 characters for
// all date-time fields (RFC3339 w/ numeric offset, e.g. 2026-07-06T03:15:42+08:00).
// Buildex therefore REQUIRES minLength=25 AND maxLength=25 on date-time fields;
// every other dex keeps the 20–25 safe window encoded by the constants below.
const DATE_LENGTH = 10;
const DATE_TIME_MIN_SAFE_MAX = 25; // maxLength must be >= this
const DATE_TIME_MAX_SAFE_MIN = 20; // minLength must be <= this
const BUILDEX_DATE_TIME_LENGTH = 25;

/**
 * Detect length limits that would break sample generation for an ISO date /
 * date-time field. Returns a single `{ key, message }` (or `null` when safe).
 * Only UNSAFE limits are flagged; absent / `''` / `null` bounds are ignored.
 *
 * @param {string|null|undefined} format
 * @param {number|string|null|undefined} minLen
 * @param {number|string|null|undefined} maxLen
 * @param {boolean|undefined} isBuildex - when true, date-time requires exactly
 *   minLength=25 and maxLength=25 (see Jul 2026 buildex note above).
 * @returns {{ key: string, message: string }|null}
 */
function getDateFormatLengthError(format, minLen, maxLen, isBuildex) {
	const minPart = isBoundPresent(minLen) ? minLen : '(none)';
	const maxPart = isBoundPresent(maxLen) ? maxLen : '(none)';

	if (format === 'date-time') {
		// Jul 2026: buildex date-time must be EXACTLY 25 characters (minLength=25 &
		// maxLength=25). Anything else — including absent bounds — is blocked.
		if (isBuildex) {
			if (Number(minLen) === BUILDEX_DATE_TIME_LENGTH && Number(maxLen) === BUILDEX_DATE_TIME_LENGTH) return null;
			return {
				key: Number(minLen) !== BUILDEX_DATE_TIME_LENGTH ? 'minLength' : 'maxLength',
				message: 'Buildex date-time fields must be exactly 25 characters. '
					+ `Got minLength=${minPart}, maxLength=${maxPart}.`,
			};
		}
		const minTooHigh = isBoundPresent(minLen) && Number(minLen) > DATE_TIME_MAX_SAFE_MIN;
		const maxTooLow = isBoundPresent(maxLen) && Number(maxLen) < DATE_TIME_MIN_SAFE_MAX;
		if (!minTooHigh && !maxTooLow) return null;
		return {
			key: minTooHigh ? 'minLength' : 'maxLength',
			message: 'Length limits on a date-time field can break sample-template '
				+ 'generation (a date-time value is 20–25 characters). Use '
				+ 'minLength ≤ 20 and maxLength ≥ 25, or remove them — the format '
				+ `already fixes the shape. Got minLength=${minPart}, maxLength=${maxPart}.`,
		};
	}

	if (format === 'date') {
		const minTooHigh = isBoundPresent(minLen) && Number(minLen) > DATE_LENGTH;
		const maxTooLow = isBoundPresent(maxLen) && Number(maxLen) < DATE_LENGTH;
		if (!minTooHigh && !maxTooLow) return null;
		return {
			key: minTooHigh ? 'minLength' : 'maxLength',
			message: 'Length limits on a date field can break sample-template '
				+ 'generation (a date value is exactly 10 characters). Use '
				+ 'minLength ≤ 10 and maxLength ≥ 10, or remove them — the format '
				+ `already fixes the shape. Got minLength=${minPart}, maxLength=${maxPart}.`,
		};
	}

	return null;
}

/**
 * Pure field-constraint validator. Inspects a single field's constraint
 * keywords and returns a flat list of `{ key, message }` problems (empty array
 * when valid). Does NOT mutate `field`; safe on frozen objects.
 *
 * `key` is the constraint input most relevant to the problem so inline error
 * display can attach the message to the right control. Values may be numbers,
 * numeric strings, `''`, or `null`; empty string / null are treated as absent.
 *
 * Shared by the base-field editor and the x-allOf rule editor so both paths
 * report identical, descriptive reasons (the offending values are embedded in
 * every message).
 *
 * @param {object} field
 * @param {boolean|undefined} isBuildex - when true, applies buildex's stricter
 *   date-time rule (see Jul 2026 note above `DATE_LENGTH`); omitted/undefined
 *   preserves prior (sgtradex) behavior.
 * @returns {{ key: string, message: string }[]}
 */
export function getFieldConstraintErrors(field, isBuildex) {
	if (!field || typeof field !== 'object') return [];
	const errors = [];

	if (field.pattern && !isRegexValid(field.pattern)) {
		errors.push({ key: 'pattern', message: 'Invalid regex pattern.' });
	}

	const minLen = field.minLength;
	const maxLen = field.maxLength;

	// date / date-time length sanity runs BEFORE the generic minLength>maxLength
	// check so its more actionable message wins when both would fire on the same
	// `minLength` control. Only UNSAFE limits are flagged — a harmless minLength
	// (<= the safe ceiling) or absent bound is never reported. A valid ISO date
	// value is exactly 10 chars; a date-time value is 20–25 chars; length limits
	// outside those ranges make json-schema-faker throw `Invalid time value`.
	const dateLengthError = getDateFormatLengthError(field.format, minLen, maxLen, isBuildex);
	if (dateLengthError) errors.push(dateLengthError);

	// Skip the generic length check when the date-format check already reported
	// the same `minLength`/`maxLength` problem — avoids a confusing double error.
	if (!dateLengthError && isBoundPresent(minLen) && isBoundPresent(maxLen)
		&& Number(minLen) > Number(maxLen)) {
		errors.push({
			key: 'minLength',
			message: `minLength (${minLen}) cannot be greater than maxLength (${maxLen}).`,
		});
	}

	const min = field.minimum;
	const max = field.maximum;
	if (isBoundPresent(min) && isBoundPresent(max) && Number(min) > Number(max)) {
		errors.push({
			key: 'minimum',
			message: `minimum (${min}) cannot be greater than maximum (${max}).`,
		});
	}

	const exMin = field.exclusiveMinimum;
	const exMax = field.exclusiveMaximum;
	if (isBoundPresent(exMin) && isBoundPresent(exMax)
		&& Number(exMin) >= Number(exMax)) {
		errors.push({
			key: 'exclusiveMinimum',
			message: `exclusiveMinimum (${exMin}) must be less than exclusiveMaximum (${exMax}).`,
		});
	}

	const minItems = field.minItems;
	const maxItems = field.maxItems;
	if (isBoundPresent(minItems) && isBoundPresent(maxItems)
		&& Number(minItems) > Number(maxItems)) {
		errors.push({
			key: 'minItems',
			message: `minItems (${minItems}) cannot be greater than maxItems (${maxItems}).`,
		});
	}

	return errors;
}

/**
 * Recursively walk a working field tree (the editor's `localFields`) and
 * collect every node whose constraints fail `getFieldConstraintErrors`.
 *
 * Each problem node is reported as `{ path, fieldName, errors }` where `path`
 * is the dotted chain of `dataElementName` segments (e.g. `parent.child`),
 * `fieldName` is the node's own `dataElementName`, and `errors` is the
 * `getFieldConstraintErrors` output. Nodes with `status === 'Deleted'` (and
 * their descendants) are skipped — a soft-deleted field is never emitted.
 *
 * Pure: does not mutate the input tree. Acts as the whole-schema catch-all so a
 * bad field the user never re-opens still can't slip through Create-Version.
 *
 * @param {object[]} fields - working field tree (each node may have `children`).
 * @param {boolean|undefined} isBuildex - forwarded to `getFieldConstraintErrors`
 *   for every node; omitted/undefined preserves prior (sgtradex) behavior.
 * @returns {{ path: string, fieldName: string, errors: { key: string, message: string }[] }[]}
 */
export function getSchemaConstraintErrors(fields, isBuildex) {
	if (!Array.isArray(fields)) return [];

	const problems = [];

	const walk = (nodes, parentPath) => {
		for (const node of nodes) {
			if (!node || typeof node !== 'object' || node.status === 'Deleted') continue;

			const fieldName = node.dataElementName || '';
			const path = parentPath ? `${parentPath}.${fieldName}` : fieldName;

			const errors = getFieldConstraintErrors(node, isBuildex);
			if (errors.length > 0) {
				problems.push({ path, fieldName, errors });
			}

			if (Array.isArray(node.children) && node.children.length > 0) {
				walk(node.children, path);
			}
		}
	};

	walk(fields, '');
	return problems;
}

function validateFieldConstraints(field, errorsByFieldKey, existsInBase, isBuildex) {
	const fieldKey = field.fieldName || '__unnamed__';
	const addFieldErr = (constraintKey, message) => {
		if (!errorsByFieldKey[fieldKey]) errorsByFieldKey[fieldKey] = {};
		if (!errorsByFieldKey[fieldKey][constraintKey]) errorsByFieldKey[fieldKey][constraintKey] = [];
		errorsByFieldKey[fieldKey][constraintKey].push(message);
	};

	for (const { key, message } of getFieldConstraintErrors(field, isBuildex)) {
		addFieldErr(key, message);
	}
	// thenFields / elseFields are partial overrides: the base schema already
	// declares the array's items, and uploaded rules from valid JSON Schema
	// commonly omit `items` in conditional branches. Only flag missing
	// arrayItemType when the field isn't present in the base schema (a
	// dangling-ref — caught separately — or a branch-only new field).
	if (field.type === 'array' && !field.arrayItemType && !existsInBase) {
		addFieldErr('arrayItemType', 'Array fields need an item type.');
	}
}

/**
 * Validate an array of x-allOf rules against the current field list.
 * Returns `{ [ruleId]: { ifConditions?, thenFields?, elseFields?, general? } }`.
 * `thenFields` / `elseFields` / `ifConditions` are keyed by fieldName / index
 * respectively, with each value a `{ [constraintKey]: string[] }` map.
 *
 * Pure function — no side effects, no React deps. Safe to call on every render
 * (consumers should debounce via useRuleValidation if needed).
 *
 * @param {object[]} rules - UI rule objects (see serialization.js).
 * @param {object[]} fields - Current localFields list (top-level fields only).
 * @param {boolean|undefined} isBuildex - forwarded to `validateFieldConstraints`
 *   for every THEN/ELSE field; omitted/undefined preserves prior (sgtradex) behavior.
 * @returns {object} errors keyed by rule id.
 */
export function validateRules(rules, fields, isBuildex) {
	const out = {};
	if (!Array.isArray(rules) || rules.length === 0) return out;

	// Phase 1b: use the flattened addressable set so nested paths
	// (e.g. `address.country`, `manifest[].consignee.email`) validate
	// without being flagged as dangling refs.
	const fieldNames = new Set(
		flattenFields(fields || []).map((a) => a.displayPath)
	);

	const seenHashes = new Map(); // hash -> ruleId

	for (const rule of rules) {
		const ruleErrors = {};
		const general = [];

		const conds = Array.isArray(rule.ifConditions) ? rule.ifConditions : [];
		if (conds.length === 0) {
			// Bug #7 carve-out: a rule with no parsed IF conditions is still valid
			// when its raw escape hatch carries a semantically meaningful IF body
			// — specifically a bare `required: [...]` (possibly inside `not`).
			// emitRule reconstructs the original schema shape from `raw.if` in
			// that case (see xallof/serialization.js Bug #7 fix). Without this
			// carve-out, the rule editor would block every schema authored
			// elsewhere whose IF uses bare `required` / `not.required` — those
			// rules are read-only in the editor (RuleCard renders a
			// schema-managed badge) but still legal.
			const rawIf = rule.raw?.if;
			const rawRequired = Array.isArray(rawIf?.required) ? rawIf.required : null;
			const rawNotRequired = Array.isArray(rawIf?.not?.required) ? rawIf.not.required : null;
			const hasRawRequired =
				(rawRequired && rawRequired.length > 0) ||
				(rawNotRequired && rawNotRequired.length > 0);
			if (!hasRawRequired) {
				general.push('Add at least one IF condition.');
			}
		}
		conds.forEach((c, idx) => {
			if (!c.field) {
				addError(ruleErrors, 'ifConditions', String(idx), 'Select a field.');
			} else if (!fieldNames.has(c.field)) {
				addError(ruleErrors, 'ifConditions', String(idx),
					`Field "${c.field}" no longer exists. Replace or remove.`);
			}
			if (OPERATOR_NEEDS_VALUE.has(c.operator)) {
				const v = c.value;
				const isEmpty = v === undefined || v === null || v === ''
					|| (Array.isArray(v) && v.length === 0);
				if (isEmpty) {
					addError(ruleErrors, 'ifConditions', String(idx), 'Enter a value.');
				}
				if (NUMERIC_OPERATORS.has(c.operator) && !Array.isArray(v) && v !== '' && v != null) {
					if (Number.isNaN(Number(v))) {
						addError(ruleErrors, 'ifConditions', String(idx), 'Value must be numeric.');
					}
				}
			}
		});

		// Empty thenFields is legal JSON Schema — `then: {}` is vacuously true
		// and emitRule preserves it. Do not block save on an empty THEN branch;
		// validation only flags THEN fields that themselves have problems.
		const thens = Array.isArray(rule.thenFields) ? rule.thenFields : [];
		const thenErrors = {};
		thens.forEach((f) => {
			if (!f.fieldName) return;
			const existsInBase = fieldNames.has(f.fieldName);
			if (!existsInBase) {
				if (!thenErrors[f.fieldName]) thenErrors[f.fieldName] = {};
				thenErrors[f.fieldName].fieldName = [
					`Field "${f.fieldName}" no longer exists. Replace or remove.`,
				];
			}
			validateFieldConstraints(f, thenErrors, existsInBase, isBuildex);
		});
		if (Object.keys(thenErrors).length > 0) ruleErrors.thenFields = thenErrors;

		const elses = Array.isArray(rule.elseFields) ? rule.elseFields : [];
		const elseErrors = {};
		elses.forEach((f) => {
			if (!f.fieldName) return;
			const existsInBase = fieldNames.has(f.fieldName);
			if (!existsInBase) {
				if (!elseErrors[f.fieldName]) elseErrors[f.fieldName] = {};
				elseErrors[f.fieldName].fieldName = [
					`Field "${f.fieldName}" no longer exists. Replace or remove.`,
				];
			}
			validateFieldConstraints(f, elseErrors, existsInBase, isBuildex);
		});
		if (Object.keys(elseErrors).length > 0) ruleErrors.elseFields = elseErrors;

		const emitted = emitRule(rule);
		if (emitted) {
			const hash = djb2Hash(JSON.stringify(canonicalize(emitted)));
			const prior = seenHashes.get(hash);
			if (prior && prior !== rule.id) {
				general.push('Duplicate of another rule.');
			} else {
				seenHashes.set(hash, rule.id);
			}
		}

		if (general.length > 0) ruleErrors.general = general;
		if (Object.keys(ruleErrors).length > 0) out[rule.id] = ruleErrors;
	}

	return out;
}

/** True when no blocking errors exist in `validateRules` output. */
export function rulesHaveBlockingErrors(errorsByRule) {
	return Object.keys(errorsByRule || {}).length > 0;
}
